import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  attachPaymentMethodThunk,
  deletePaymentMethodThunk,
  fetchPaymentMethodsThunk,
  selectBillingState,
  setDefaultPaymentMethodThunk,
} from "../store/billingSlice";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { isAdmin } from "../../../shared/utils/roles";

// Payment methods are managed by Stripe. Cards are added through Stripe's
// hosted payment flow when an invoice is paid, so this hook only lists the
// saved methods and lets admins detach them.
export const usePaymentMethods = () => {
  const dispatch = useDispatch();
  const state = useSelector(selectBillingState);
  const currentUser = useSelector(selectCurrentUser);
  const canEditPaymentMethods = isAdmin(currentUser?.user_role);

  useEffect(() => {
    if (state.hasPaymentMethodsLoaded) return;
    dispatch(fetchPaymentMethodsThunk());
  }, [dispatch, state.hasPaymentMethodsLoaded]);

  const handleDelete = useCallback(
    async (stripePmId) => {
      if (!canEditPaymentMethods) {
        return false;
      }

      const action = await dispatch(deletePaymentMethodThunk(stripePmId));
      return deletePaymentMethodThunk.fulfilled.match(action);
    },
    [canEditPaymentMethods, dispatch],
  );

  const handleAttach = useCallback(
    async (stripePmId, isDefault = false) => {
      if (!canEditPaymentMethods) {
        return { error: { message: "Payment methods are read-only for this role." } };
      }

      const action = await dispatch(attachPaymentMethodThunk({ stripePmId, isDefault }));
      if (attachPaymentMethodThunk.fulfilled.match(action)) {
        return { success: true };
      }

      return { error: { message: action.payload || "Unable to add payment method." } };
    },
    [canEditPaymentMethods, dispatch],
  );

  const handleSetDefault = useCallback(
    async (stripePmId) => {
      if (!canEditPaymentMethods) {
        return false;
      }

      const action = await dispatch(setDefaultPaymentMethodThunk({ stripePmId }));
      return setDefaultPaymentMethodThunk.fulfilled.match(action);
    },
    [canEditPaymentMethods, dispatch],
  );

  return {
    cards: state.cards,
    isLoading: state.isLoadingPaymentMethods,
    isAttaching: state.isAttachingPaymentMethod,
    isDeleting: state.isDeletingPaymentMethod,
    settingDefaultId: state.settingDefaultPaymentMethodId,
    apiError: state.paymentMethodsError,
    canEditPaymentMethods,
    handleAttach,
    handleDelete,
    handleSetDefault,
  };
};

export default usePaymentMethods;
